'use client';

import { memo, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Film,
  PenTool,
  BarChart3,
  MessageSquare,
  Zap,
  Search,
  Music,
  Image,
  type LucideIcon,
} from 'lucide-react';

interface Category {
  id: string;
  name: string;
  icon: LucideIcon;
  color: string;
  count?: number;
}

// Category definitions with unique color accents
export const CATEGORY_DEFINITIONS: Category[] = [
  { id: 'content', name: 'Content', icon: Film, color: '#FF6B6B', count: 12 },
  { id: 'writing', name: 'Writing', icon: PenTool, color: '#4ECDC4', count: 8 },
  { id: 'analytics', name: 'Analytics', icon: BarChart3, color: '#9B59B6', count: 6 },
  { id: 'social', name: 'Social', icon: MessageSquare, color: '#F39C12', count: 10 },
  { id: 'automation', name: 'Automation', icon: Zap, color: '#00D4FF', count: 7 },
  { id: 'research', name: 'Research', icon: Search, color: '#2ECC71', count: 5 },
  { id: 'audio', name: 'Audio', icon: Music, color: '#E91E63', count: 4 },
  { id: 'visual', name: 'Visual', icon: Image, color: '#FF9800', count: 9 },
];

interface OrbitingCategoriesProps {
  /** Number of categories to show (default: all) */
  count?: number;
  /** Size of the orbit */
  size?: 'sm' | 'md' | 'lg';
  /** Animation speed multiplier */
  speed?: number;
  /** Whether to show category labels */
  showLabels?: boolean;
  /** Whether to show category counts */
  showCounts?: boolean;
  /** Custom class name */
  className?: string;
  /** Optional click handler */
  onCategoryClick?: (category: Category) => void;
}

const SIZE_CONFIG = {
  sm: { radius: 60, iconSize: 18, container: 160 },
  md: { radius: 100, iconSize: 22, container: 240 },
  lg: { radius: 140, iconSize: 26, container: 320 },
};

function OrbitingCategoriesComponent({
  count,
  size = 'md',
  speed = 1,
  showLabels = false,
  showCounts = false,
  className = '',
  onCategoryClick,
}: OrbitingCategoriesProps) {
  const config = SIZE_CONFIG[size];
  const categories = useMemo(
    () => (count ? CATEGORY_DEFINITIONS.slice(0, count) : CATEGORY_DEFINITIONS),
    [count]
  );

  const angleStep = (2 * Math.PI) / categories.length;

  return (
    <div
      className={`relative max-w-full ${className}`}
      style={{
        width: config.container,
        height: config.container,
        maxWidth: '100%',
        aspectRatio: '1 / 1',
      }}
    >
      {/* Orbit ring */}
      <motion.div
        className="absolute inset-0 rounded-full border border-white/10"
        style={{
          left: '50%',
          top: '50%',
          width: config.radius * 2,
          height: config.radius * 2,
          marginLeft: -config.radius,
          marginTop: -config.radius,
        }}
        animate={{ rotate: 360 }}
        transition={{
          duration: 60 / speed,
          repeat: Infinity,
          ease: 'linear',
        }}
      />

      {/* Dashed inner ring */}
      <div
        className="absolute rounded-full border border-dashed border-white/5"
        style={{
          left: '50%',
          top: '50%',
          width: config.radius * 1.4,
          height: config.radius * 1.4,
          marginLeft: -config.radius * 0.7,
          marginTop: -config.radius * 0.7,
        }}
      />

      {/* Category icons */}
      {categories.map((category, index) => {
        const baseAngle = index * angleStep;
        const Icon = category.icon;

        return (
          <motion.div
            key={category.id}
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
            }}
            animate={{
              x: Math.cos(baseAngle) * config.radius - config.iconSize,
              y: Math.sin(baseAngle) * config.radius - config.iconSize,
            }}
            whileHover={{ scale: 1.2 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20,
            }}
          >
            <motion.button
              className="group relative flex items-center justify-center cursor-pointer"
              style={{
                width: config.iconSize * 2,
                height: config.iconSize * 2,
              }}
              onClick={() => onCategoryClick?.(category)}
              animate={{
                y: [0, -4, 0],
              }}
              transition={{
                duration: 2 + index * 0.2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: index * 0.1,
              }}
              aria-label={category.name}
            >
              {/* Background glow */}
              <div
                className="absolute inset-0 rounded-xl opacity-20 group-hover:opacity-40 transition-opacity"
                style={{
                  background: `radial-gradient(circle, ${category.color}, transparent)`,
                }}
              />

              {/* Icon container */}
              <div
                className="relative flex items-center justify-center rounded-xl backdrop-blur-xs transition-all group-hover:scale-110"
                style={{
                  width: config.iconSize * 2,
                  height: config.iconSize * 2,
                  background: `linear-gradient(135deg, ${category.color}20, ${category.color}10)`,
                  border: `1px solid ${category.color}40`,
                  boxShadow: `0 4px 16px -4px ${category.color}30`,
                }}
              >
                <Icon
                  size={config.iconSize}
                  style={{ color: category.color }}
                  strokeWidth={1.5}
                />
              </div>

              {/* Tooltip label */}
              {showLabels && (
                <div
                  className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background: `${category.color}20`,
                    color: category.color,
                    border: `1px solid ${category.color}30`,
                  }}
                >
                  {category.name}
                  {showCounts && category.count && (
                    <span className="ml-1 opacity-70">({category.count})</span>
                  )}
                </div>
              )}

              {/* Count badge */}
              {showCounts && category.count && !showLabels && (
                <div
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold"
                  style={{
                    background: category.color,
                    color: '#0a0a0f',
                  }}
                >
                  {category.count}
                </div>
              )}
            </motion.button>
          </motion.div>
        );
      })}

      {/* Center pulse */}
      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: 40,
          height: 40,
          background: 'radial-gradient(circle, rgba(0,212,255,0.3), transparent)',
        }}
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.5, 0.2, 0.5],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </div>
  );
}

export const OrbitingCategories = memo(OrbitingCategoriesComponent);

// Export category definitions for use in other components
export { type Category };
